"use client";
import React, { useEffect, useState, useCallback, useMemo, memo } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import logo from "@/assets/images/logo.svg";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
// Using inline SVG icons instead of external images
import AuthButton from "./AuthButton";
import CustomButton from "@/components/common/CustomButton";
import NavBottom from "./BlogNavBottom";
import { Turn as Hamburger } from "hamburger-react";
import { CategoryFallback } from "@/utils/categoryFallback";
import {
  BookDemoHubspotButtonUrl,
  LoginHubspotButtonUrl,
  SignUpHubspotButtonUrl,
} from "@/utils/hubspot-contants";

const BlogNavBar = () => {
  const router = useRouter();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandableArea, setExpandableArea] = useState(0);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [category, setCategory] = useState<
    Array<{
      title: string;
      slug: string;
      subcategory_detail: Array<{ title: string; slug: string }>;
    }>
  >([]);

  const categorydatafetch = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BASE_URL}/api/v1/category/`,
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch categories: ${response.status}`);
      }
      const data = await response.json();
      setCategory(data.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, []);

  useEffect(() => {
    categorydatafetch();
  }, [categorydatafetch]);

  const toggleDrawer = useCallback(() => {
    setDrawerOpen((prev) => !prev);
  }, []);

  const closeDrawerAndNavigate = useCallback(
    (href: string) => {
      setDrawerOpen(false);
      router.push(href);
    },
    [router],
  );

  const handleCategoryHover = useCallback((categoryTitle: string) => {
    setActiveCategory(categoryTitle);
  }, []);

  const handleCategoryClick = useCallback((categoryTitle: string) => {
    setActiveCategory((prev) => {
      if (prev === categoryTitle) {
        setExpandableArea(0);
        return null;
      }
      return categoryTitle;
    });
  }, []);

  const handleMouseLeaveNavbar = useCallback(() => {
    if (!activeCategory) {
      setExpandableArea(0);
    }
  }, [activeCategory]);

  const handleNavBottomMouseLeave = useCallback(() => {
    setActiveCategory(null);
    setExpandableArea(0);
  }, []);

  const generateAccordionItems = useMemo(() => {
    return category.map((categoryItem, index) => ({
      key: `category-${index}`,
      label: categoryItem.title,
      children: (
        <ul className="mobile-ul">
          {categoryItem.subcategory_detail.map(
            (
              subcategory: { title: string; slug: string },
              subIndex: number,
            ) => (
              <li key={subIndex}>
                <button
                  className="block w-full text-left text-[14px] font-medium text-[#343434] transition duration-300 ease-in-out hover:text-[#6049E7]"
                  onClick={() =>
                    closeDrawerAndNavigate(`/blog/${subcategory.slug}`)
                  }
                >
                  <div className="flex items-center">{subcategory.title}</div>
                </button>
              </li>
            ),
          )}
        </ul>
      ),
    }));
  }, [category, closeDrawerAndNavigate]);

  const filteredCategories = useMemo(() => {
    return category;
  }, [category]);

  return (
    <>
      <div className="fixed left-0 top-[6px] z-[500] w-full">
        <div className="navbar--container container relative z-40 mx-auto hidden px-4 xl:block">
          <div
            style={{
              height: expandableArea ? `${expandableArea}px` : "56px",
            }}
            className={`px-4 py-2 ${expandableArea
              ? "bg-[#FAFAFA]"
              : "bg-[#ffffffa8] backdrop-blur-[24px]"
              } flex flex-col overflow-hidden rounded-[8px] duration-300 ease-in-out`}
            onMouseLeave={handleMouseLeaveNavbar}
          >
            <div className="flex items-center justify-between">
              <Link href={"/"} className="block w-[130px]">
                <Image
                  src={logo}
                  quality={100}
                  alt="Kroolo - AI-powered productivity platform"
                  width={130}
                  height={40}
                  className="object-contain"
                />
              </Link>
              <div className="flex items-center gap-8">
                <Link
                  href={`/blog`}
                  className="text-[14px] font-medium text-[#343434] transition duration-300 ease-in-out hover:text-[#6049E7]"
                >
                  All Blogs
                </Link>
                {filteredCategories.length ? (
                  filteredCategories.map((item) => (
                    <div
                      key={item.slug}
                      className="group relative"
                      onMouseEnter={() => handleCategoryHover(item.title)}
                      onClick={() => handleCategoryClick(item.title)}
                    >
                      <div className="flex cursor-pointer items-center">
                        <Link
                          className={`text-[14px] font-medium transition-colors duration-200 ${activeCategory === item.title
                            ? "text-[#6049E7]"
                            : "text-[#343434] hover:text-[#6049E7]"
                            }`}
                          href={`/blog/${item.slug}`}
                        >
                          {item.title}
                        </Link>
                        <svg
                          className={`ms-3 h-3 w-3 transition-all duration-200 ${activeCategory === item.title
                            ? "rotate-180 text-[#6049E7]"
                            : "text-[#343434]"
                            }`}
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 10 6"
                        >
                          <path
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="m1 1 4 4 4-4"
                          />
                        </svg>
                      </div>
                    </div>
                  ))
                ) : (
                  <CategoryFallback isNavbar />
                )}
              </div>
              <div className="flex items-center gap-4">
                <CustomButton
                  text="Book Demo"
                  url={BookDemoHubspotButtonUrl}
                  variant="secondary"
                  version="black"
                />
                <div className="flex items-center gap-4">
                  <CustomButton
                    text="Login"
                    url={LoginHubspotButtonUrl}
                    variant="secondary"
                    version="black"
                  />
                  <CustomButton
                    text="Sign Up"
                    url={SignUpHubspotButtonUrl}
                    variant="primary"
                    version="black"
                  />
                </div>
              </div>
            </div>
            {activeCategory && (
              <NavBottom
                activeCategory={activeCategory}
                setActiveCategory={setActiveCategory}
                setExpandableArea={setExpandableArea}
                category={category}
                onMouseLeave={handleNavBottomMouseLeave}
              />
            )}
          </div>
        </div>
        <div className="container relative z-40 mx-auto block px-4 xl:hidden">
          <div
            className={`${drawerOpen
              ? "h-fit overflow-y-auto bg-[#FAFAFA]"
              : "h-[58px] overflow-hidden bg-[#ffffffa8]"
              } rounded-lg border-[0.25px] border-[#8C8C8C] px-3 py-1 backdrop-blur-[24px] duration-300 ease-in-out md:px-5`}
          >
            <div className="flex items-center justify-between">
              <Link href={"/"} className="block w-[130px]">
                <Image
                  src={logo}
                  quality={100}
                  alt="Kroolo - AI-powered productivity platform"
                  width={130}
                  height={40}
                  className="object-contain"
                />
              </Link>
              <div className="xl:hidden">
                <Hamburger
                  toggled={drawerOpen}
                  toggle={setDrawerOpen}
                  size={20}
                  direction="right"
                  color="#343434"
                  label={
                    drawerOpen
                      ? "Close blog navigation menu"
                      : "Open blog navigation menu"
                  }
                />
              </div>
            </div>
            {drawerOpen && (
              <div className="mt-4">
                <div className="flex w-full items-center justify-between border-b-[0.25px] border-[#8C8C8C] py-[8px] xl:hidden">
                  <div className="flex items-center justify-start gap-0 max-[440px]:!w-full max-[440px]:!justify-between min-[440px]:!gap-2">
                    <CustomButton
                      text="Login"
                      url={LoginHubspotButtonUrl}
                      variant="secondary"
                      version="black"
                    />
                    <CustomButton
                      text="Sign Up"
                      url={SignUpHubspotButtonUrl}
                      variant="primary"
                      version="black"
                    />
                  </div>
                  <CustomButton
                    text="Book Demo"
                    url={BookDemoHubspotButtonUrl}
                    variant="secondary"
                    version="black"
                  />
                </div>
                <ul className="font-medium xl:mt-0 xl:flex-row xl:space-x-8 xl:border-0 rtl:space-x-reverse">
                  <button
                    className="block w-fit text-left text-[14px] font-medium text-[#343434] transition duration-300 ease-in-out hover:text-[#6049E7]"
                    onClick={() => closeDrawerAndNavigate(`/blog`)}
                  >
                    <div className="flex items-center pb-2 pt-4">Home</div>
                  </button>
                  <Accordion type="single" collapsible className="w-full">
                    {generateAccordionItems.map((item) => (
                      <AccordionItem
                        key={item.key}
                        value={item.key}
                        className="border-none"
                      >
                        <AccordionTrigger className="py-2 text-[14px] font-medium text-[#343434] hover:text-[#6049E7] hover:no-underline">
                          {item.label}
                        </AccordionTrigger>
                        <AccordionContent className="pb-2">
                          {item.children}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                  <button
                    className="hidden w-fit text-left text-[14px] font-medium text-[#343434] transition duration-300 ease-in-out hover:text-[#6049E7] max-[440px]:flex"
                    onClick={() =>
                      closeDrawerAndNavigate(BookDemoHubspotButtonUrl)
                    }
                  >
                    <div className="flex items-center pb-4 pt-2">
                      Book Demo
                    </div>
                  </button>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="navbar--container--blur h-[70px]"></div>
      <div
        className={`fixed left-0 top-0 z-[400] hidden h-full w-full bg-[rgba(16,18,20,0.14)] backdrop-blur-[10px] duration-300 ease-in-out lg:block ${expandableArea ? "visible opacity-100" : "invisible opacity-0"
          } `}
      ></div>
    </>
  );
};

export default memo(BlogNavBar);
